import { nanoid } from 'nanoid';
import { useState, useCallback } from 'react';
import { TypingDots } from '../chat/TypingDots';

export interface ChatMessage {
  id: string;
  from: 'bot' | 'user' | 'system';
  content: React.ReactNode;
}

interface SayOptions {
  typingMs?: number;
  simulateTyping?: boolean;
  staggerMs?: number;
  postMessagePauseMs?: number;
}

export function useChatMessages() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const addMessage = useCallback(
    (from: ChatMessage['from'], content: React.ReactNode): string => {
      const id = nanoid();
      setMessages((prev) => [...prev, { id, from, content }]);
      return id;
    },
    []
  );

  const updateMessage = useCallback((id: string, content: React.ReactNode) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, content } : m))
    );
  }, []);

  const say = useCallback(
    async (texts: React.ReactNode[], options?: SayOptions) => {
      const typingMs = options?.typingMs ?? 600;
      const simulateTyping = options?.simulateTyping ?? false;
      const staggerMs = options?.staggerMs ?? 180;
      const postMessagePauseMs = options?.postMessagePauseMs ?? 120;

      for (const t of texts) {
        if (simulateTyping) {
          const id = nanoid();
          setMessages((prev) => [
            ...prev,
            {
              id,
              from: 'bot',
              content: (
                <div className='flex items-center gap-1'>
                  <TypingDots />
                </div>
              ),
            },
          ]);
          await new Promise((r) => setTimeout(r, typingMs));
          setMessages((prev) =>
            prev.map((m) => (m.id === id ? { ...m, content: t } : m))
          );
          if (postMessagePauseMs > 0) {
            await new Promise((r) => setTimeout(r, postMessagePauseMs));
          }
        } else {
          if (staggerMs > 0) {
            await new Promise((r) => setTimeout(r, 250));
          }
          setMessages((prev) => [
            ...prev,
            { id: nanoid(), from: 'bot', content: t },
          ]);
        }
      }
    },
    []
  );

  return {
    messages,
    setMessages,
    addMessage,
    updateMessage,
    say,
  };
}
